/** "Your Connection" card — IP details / Location / System tabs. Location tab
 *  also renders the inline map. System tab shows real, live browser signals. */
import { useEffect, useState } from "react";
import type { Connection, Phase, ResolverRow, Tab } from "../lib/types";
import { detectUa } from "../lib/ua";
import { webrtcPublicIp } from "../lib/webrtc";
import { LocationMap } from "./LocationMap";

interface YourConnectionProps {
  connection: Connection | null;
  tab: Tab;
  onTab: (t: Tab) => void;
  /** Resolvers observed by the current test — the user's actual DNS servers. */
  servers: ResolverRow[];
  phase: Phase;
}

const BADGE_STYLE: Record<ResolverRow["badge"], { color: string; bg: string; border: string }> = {
  RAPIDTUNNEL: { color: "#2BE82B", bg: "rgba(43,232,43,0.08)", border: "rgba(43,232,43,0.35)" },
  "YOUR NETWORK": { color: "#2BE82B", bg: "rgba(43,232,43,0.08)", border: "rgba(43,232,43,0.35)" },
  "PUBLIC DNS": { color: "#2BE82B", bg: "rgba(43,232,43,0.08)", border: "rgba(43,232,43,0.35)" },
  "OTHER NETWORK": { color: "#DCF300", bg: "rgba(220,243,0,0.07)", border: "rgba(220,243,0,0.3)" },
};

interface Row {
  k: string;
  v: string;
  flag?: string;
  note?: string;
  /** ok = green check, warn = yellow exclamation. */
  status?: "ok" | "warn";
}

function StatusIcon({ status }: { status: "ok" | "warn" }) {
  const ok = status === "ok";
  const color = ok ? "#2BE82B" : "#DCF300";
  return (
    <svg width={14} height={14} viewBox="0 0 14 14" aria-label={ok ? "ok" : "warning"} style={{ flex: "none" }}>
      <circle cx={7} cy={7} r={7} fill={color} />
      {ok ? (
        <path d="M4 7.2l2 2 4-4.2" fill="none" stroke="#0A0B0A" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <g fill="#0A0B0A"><rect x={6.2} y={3.2} width={1.6} height={5} rx={0.8} /><circle cx={7} cy={10.4} r={0.95} /></g>
      )}
    </svg>
  );
}

interface SystemInfo {
  browser: string;
  platform: string;
  webrtc: string;
  doh: string;
}

function useSystemInfo(publicIp: string | undefined): SystemInfo {
  const [info, setInfo] = useState<SystemInfo>({ browser: "…", platform: "…", webrtc: "checking…", doh: "…" });
  useEffect(() => {
    let alive = true;
    (async () => {
      const ua = await detectUa();
      if (alive) setInfo((s) => ({ ...s, browser: ua.browser, platform: ua.platform, doh: "supported" }));
      const rtcIp = await webrtcPublicIp();
      if (!alive) return;
      const leak = rtcIp && publicIp && rtcIp !== publicIp;
      setInfo((s) => ({ ...s, webrtc: rtcIp ? (leak ? `leak: ${rtcIp}` : "no leak detected") : "not exposed" }));
    })();
    return () => { alive = false; };
  }, [publicIp]);
  return info;
}

const TABS: { id: Tab; label: string }[] = [
  { id: "ip", label: "IP & DNS" },
  { id: "loc", label: "Location" },
  { id: "sys", label: "Device Info" },
];

/** Cymru returns "HANDLE - Long Name"; show the short handle like most tools do. */
function ispLabel(r: ResolverRow): string {
  const org = r.org?.trim();
  if (org) return org.split(" - ")[0]?.trim() || org;
  if (r.asn) return r.asn;
  const labels = r.hostname.split(".");
  return labels.length >= 2 ? (labels[labels.length - 2] ?? r.hostname) : r.hostname;
}

export function YourConnection({ connection, tab, onTab, servers, phase }: YourConnectionProps) {
  const sys = useSystemInfo(connection?.ip);
  const c = connection;

  const rows: Row[] =
    tab === "ip"
      ? [
          { k: "ip address", v: c?.ip ?? "—", flag: c?.flag },
          { k: "operator", v: c?.org ?? "—", note: c?.asn },
        ]
      : tab === "loc"
        ? [
            { k: "city", v: c?.city ?? "—" },
            { k: "region", v: c?.region ?? "—" },
            { k: "country", v: c?.country ?? "—", flag: c?.flag },
            { k: "postal", v: c?.postal ?? "—" },
          ]
        : [
            { k: "browser", v: sys.browser },
            { k: "platform", v: sys.platform },
            { k: "webrtc", v: sys.webrtc, status: sys.webrtc === "checking…" ? undefined : sys.webrtc.startsWith("leak") ? "warn" : "ok" },
            { k: "doh support", v: sys.doh, status: sys.doh === "…" ? undefined : sys.doh === "supported" ? "ok" : "warn" },
          ];

  return (
    <div
      data-rt-card=""
      style={{ width: "100%", boxSizing: "border-box", marginTop: 24, background: "#0A0B0A", border: "1px solid #2A2D29", borderRadius: 26, padding: "24px 26px", display: "flex", flexDirection: "column", gap: 12 }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <span style={{ fontFamily: "'Sora', sans-serif", fontSize: 16, fontWeight: 700, color: "#DEDFDF" }}>Your Connection</span>
        <div data-rt-tabs="" style={{ display: "flex", gap: 4, background: "#141614", border: "1px solid #2A2D29", borderRadius: 999, padding: 3 }}>
          {TABS.map((t) => (
            <button key={t.id} data-tab="" data-active={tab === t.id ? "on" : "off"} onClick={() => onTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column" }}>
        {rows.map((row) => (
          <div key={row.k} data-rt-connrow="" style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 14, alignItems: "center", padding: "13px 0", borderBottom: "1px solid #1C1E1B" }}>
            <span style={{ fontSize: 12, color: "#5C5F5A" }}>{row.k}</span>
            <span style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: "'Space Mono', monospace", fontSize: 13, color: "#DEDFDF", flexWrap: "wrap" }}>
              {row.status && <StatusIcon status={row.status} />}
              {row.flag && <span style={{ fontSize: 15, lineHeight: 1 }}>{row.flag}</span>}
              {row.v}
              {row.note && <span style={{ fontSize: 11, color: "#5C5F5A" }}>{row.note}</span>}
            </span>
          </div>
        ))}
      </div>

      {tab === "ip" && (
        <div style={{ display: "flex", flexDirection: "column", marginTop: 6 }}>
          <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: "#B9BCB6", padding: "12px 0 8px" }}>DNS servers</span>
          <p style={{ margin: "0 0 10px 0", fontSize: 12.5, lineHeight: 1.65, color: "#8A8D88" }}>
            Below are the DNS resolvers your device is using. If you see your local ISP as the resolver, you should change this immediately. We recommend using{" "}
            <a href="https://one.one.one.one/dns/" target="_blank" rel="noopener noreferrer">Cloudflare DNS</a> or{" "}
            <a href="https://www.quad9.net/" target="_blank" rel="noopener noreferrer">Quad9</a>.
          </p>
          {servers.length === 0 ? (
            <div style={{ padding: "18px 0", fontSize: 12.5, lineHeight: 1.6, color: "#8A8D88" }}>
              {phase === "running"
                ? "Detecting your DNS servers…"
                : phase === "done"
                  ? "No DNS server contacted our nameservers during the test, so none could be identified."
                  : "Run the leak test to detect which DNS servers your device actually uses. We don't read your device settings — we report the servers that contact our nameservers."}
            </div>
          ) : (
            <>
              <div data-rt-dnsrow="" style={{ display: "grid", gridTemplateColumns: "90px 1fr 1fr auto", gap: 14, padding: "10px 0", borderBottom: "1px solid #2A2D29", fontSize: 10, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: "#B9BCB6" }}>
                <span>Country</span><span>ISP</span><span>DNS Server</span><span>Role</span>
              </div>
              {servers.map((r) => {
                const b = BADGE_STYLE[r.badge];
                return (
                  <div key={r.ip} data-rt-dnsrow="" style={{ display: "grid", gridTemplateColumns: "90px 1fr 1fr auto", gap: 14, alignItems: "center", padding: "13px 0", borderBottom: "1px solid #1C1E1B" }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "#B9BCB6" }}>
                      <span style={{ fontSize: 15, lineHeight: 1 }}>{r.flag}</span>{r.cc || "—"}
                    </span>
                    <span style={{ fontSize: 12.5, color: "#DEDFDF", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.publicName ?? ispLabel(r)}</span>
                    <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 13, color: "#DEDFDF", overflowWrap: "anywhere" }}>{r.ip}</span>
                    <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.12em", color: b.color, background: b.bg, border: `1px solid ${b.border}`, borderRadius: 999, padding: "4px 9px", whiteSpace: "nowrap" }}>{r.badge}</span>
                  </div>
                );
              })}
              <div style={{ paddingTop: 8, fontSize: 12, lineHeight: 1.6, color: "#5C5F5A" }}>
                {servers.some((r) => r.ecs)
                  ? `Privacy note: your resolver forwards part of your address (EDNS Client Subnet ${servers.find((r) => r.ecs)?.ecs}) to the sites you look up. Quad9 and most privacy resolvers do not.`
                  : "Privacy note: your resolver does not forward your address (no EDNS Client Subnet) to the sites you look up — good."}
                {" "}Detected by observing which resolvers contacted our nameservers during your test — not by reading your device settings.
              </div>
            </>
          )}
        </div>
      )}

      {tab === "loc" && <LocationMap lat={c?.lat ?? null} lon={c?.lon ?? null} city={c?.city ?? ""} cc={c?.cc ?? ""} />}
    </div>
  );
}
